import axios from 'axios';

const API_URL = 'https://api.openf1.org/v1';
// Use environment variable if set, otherwise use relative path (empty string)
// This allows the app to work with relative URLs when served from the same domain
const SERVER_URL = process.env.REACT_APP_SERVER_URL || (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5000');

// Get the latest season year
export const getLatestSeason = async () => {
  try {
    const currentYear = new Date().getFullYear();
    // Try current year first, then go back if no data
    for (let year = currentYear; year >= 2020; year--) {
      const response = await axios.get(`${API_URL}/sessions`, {
        params: {
          year: year,
          session_name: 'Race'
        }
      });
      if (response.data && response.data.length > 0) {
        return year;
      }
    }
    return currentYear; // Fallback
  } catch (error) {
    console.error('Error fetching latest season:', error);
    return new Date().getFullYear(); // Fallback to current year
  }
};

// Get all tracks/races for the latest season
export const getAllTracks = async () => {
  try {
    const latestYear = await getLatestSeason();
    const response = await axios.get(`${API_URL}/sessions`, {
      params: {
        year: latestYear,
        session_name: 'Race'
      }
    });
    
    if (!response.data || !Array.isArray(response.data)) {
      console.warn('No track data received or invalid format');
      return [];
    }
    
    // Get unique tracks
    const tracksMap = new Map();
    response.data.forEach(session => {
      if (session && session.country_name) {
        const key = session.country_name;
        if (!tracksMap.has(key)) {
          tracksMap.set(key, {
            country_name: session.country_name,
            track_name: session.location || session.circuit_short_name || session.country_name,
            year: latestYear
          });
        }
      }
    });
    
    return Array.from(tracksMap.values()).sort((a, b) => 
      a.track_name.localeCompare(b.track_name)
    );
  } catch (error) {
    console.error('Error fetching tracks:', error);
    return [];
  }
};

// Get all drivers for a specific session
export const getAllDrivers = async (sessionKey) => {
  try {
    if (!sessionKey) {
      console.warn('No session key provided for fetching drivers');
      return [];
    }
    
    const response = await axios.get(`${API_URL}/drivers`, {
      params: {
        session_key: sessionKey
      }
    });
    
    if (!response.data || !Array.isArray(response.data)) {
      console.warn('No driver data received or invalid format');
      return [];
    }
    
    return response.data
      .filter(driver => driver && driver.driver_number)
      .map(driver => ({
        id: driver.driver_number,
        name: `${driver.first_name || ''} ${driver.last_name || ''}`.trim() || `Driver ${driver.driver_number}`,
        code: driver.name_acronym || '',
        team: driver.team_name || ''
      }));
  } catch (error) {
    console.error('Error fetching drivers:', error);
    return [];
  }
};

// Get the latest session (race) for a track
export const getLatestSession = async (selectedTrack, year = null) => {
  try {
    if (!selectedTrack) {
      throw new Error('No track selected');
    }
    
    const seasonYear = year || await getLatestSeason();
    const response = await axios.get(`${API_URL}/sessions`, {
      params: {
        country_name: selectedTrack,
        session_name: 'Race',
        year: seasonYear
      }
    });
    
    if (!response.data || !Array.isArray(response.data) || response.data.length === 0) {
      throw new Error(`No race session found for ${selectedTrack} in ${seasonYear}`);
    }
    
    // Return the most recent session (first one should be latest)
    const session = response.data[0];
    if (!session || !session.session_key) {
      throw new Error(`Invalid session data for ${selectedTrack}`);
    }
    
    return session;
  } catch (error) {
    console.error('Error fetching session:', error);
    throw error;
  }
};

// Get driver lap data
export const getDriverLapData = async (driverNumber, lapNumber, selectedTrack) => {
  try {
    // Await the session data properly
    const sessionData = await getLatestSession(selectedTrack); // Await the result of getLatestSession
    const sessionKey = sessionData.session_key; // Access the session key
    console.log('Session Key:', sessionKey);

    const response = await axios.get(`${SERVER_URL}/api/laps`, {
      params: {
        driverNumber,
        lapNumber,
        sessionKey,
      },
    });
    console.log(response.data);
    return response.data;
  } catch (error) {
    console.error(`Error fetching lap data for driver ${driverNumber}:`, error);
    throw error;
  }
};

export const getDriverInfo = async (driverNumber) => {
  try {
      const sessionData = await getLatestSession(); // Await the result of getLatestSession
      const sessionKey = sessionData.session_key; // Access the session key
      console.log('Session Key:', sessionKey);
      const response = await axios.get(`${API_URL}/drivers`, {
          params: {
              driver_number: driverNumber,  // Passing driver_number to the API
              session_key: sessionKey  // You can dynamically fetch session_key if needed
          },
      });

      console.log(response.data);
      return response.data;  // Returns driver info like name, headshot URL, etc.
  } catch (error) {
      console.error("Error fetching driver info:", error);
      throw error;
  }
};

// Get multiple laps for a driver (range)
export const getDriverLapsRange = async (driverNumber, startLap, endLap, selectedTrack) => {
  try {
    const sessionData = await getLatestSession(selectedTrack);
    const sessionKey = sessionData.session_key;

    const response = await axios.get(`${SERVER_URL}/api/laps/range`, {
      params: {
        driverNumber,
        startLap,
        endLap,
        sessionKey,
      },
    });
    return response.data;
  } catch (error) {
    console.error(`Error fetching lap range for driver ${driverNumber}:`, error);
    throw error;
  }
};

// Get all laps for a driver
export const getAllDriverLaps = async (driverNumber, selectedTrack) => {
  try {
    const sessionData = await getLatestSession(selectedTrack);
    if (!sessionData || !sessionData.session_key) {
      throw new Error(`No session found for track: ${selectedTrack}`);
    }
    const sessionKey = sessionData.session_key;

    const response = await axios.get(`${SERVER_URL}/api/laps/range`, {
      params: {
        driverNumber,
        sessionKey,
      },
    });
    return response.data || [];
  } catch (error) {
    console.error(`Error fetching all laps for driver ${driverNumber}:`, error);
    if (error.response) {
      console.error('Response error:', error.response.status, error.response.data);
    } else if (error.request) {
      console.error('No response received:', error.request);
    } else {
      console.error('Error setting up request:', error.message);
    }
    throw error;
  }
};

// Get driver position data
export const getDriverPositions = async (driverNumber, selectedTrack) => {
  try {
    const sessionData = await getLatestSession(selectedTrack);
    const sessionKey = sessionData.session_key;

    const response = await axios.get(`${SERVER_URL}/api/positions`, {
      params: {
        driverNumber,
        sessionKey,
      },
    });
    return response.data;
  } catch (error) {
    console.error(`Error fetching position data for driver ${driverNumber}:`, error);
    throw error;
  }
};

// Get tire compound data
export const getTireData = async (driverNumber, selectedTrack) => {
  try {
    const sessionData = await getLatestSession(selectedTrack);
    const sessionKey = sessionData.session_key;

    const response = await axios.get(`${SERVER_URL}/api/tires`, {
      params: {
        driverNumber,
        sessionKey,
      },
    });
    return response.data;
  } catch (error) {
    console.error(`Error fetching tire data for driver ${driverNumber}:`, error);
    throw error;
  }
};